import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Mail, CheckCircle, Home, Heart, ShoppingBag, Clock } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_loyaltyhub-3/artifacts/bd853roq_La%20petite%20e%CC%81picerie-logo.png";

// Composant étoiles spatiales animées
const SpaceStars = () => {
  const stars = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    size: Math.random() * 3 + 1,
    delay: Math.random() * 3,
    duration: Math.random() * 2 + 2
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {stars.map((star) => (
        <motion.div
          key={star.id}
          className="absolute rounded-full bg-white"
          style={{
            left: star.left,
            top: star.top,
            width: star.size,
            height: star.size,
          }}
          animate={{
            opacity: [0.2, 1, 0.2],
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: star.duration,
            delay: star.delay,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      ))}
    </div>
  );
};

// Composant particules de confettis
const Confetti = () => {
  const confettis = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    color: ['#FFD700', '#FF6B6B', '#00FF00', '#00FFFF', '#FF00FF', '#FFA500'][Math.floor(Math.random() * 6)],
    delay: Math.random() * 2,
    duration: Math.random() * 3 + 2
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {confettis.map((confetti) => (
        <motion.div
          key={confetti.id}
          className="absolute w-3 h-3 rounded-sm"
          style={{
            left: confetti.left,
            backgroundColor: confetti.color,
            top: -20,
          }}
          animate={{
            y: ['0vh', '110vh'],
            rotate: [0, 360],
            opacity: [1, 0.8, 0]
          }}
          transition={{
            duration: confetti.duration,
            delay: confetti.delay,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      ))}
    </div>
  );
};

export default function MerciPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(10);
  const [showConfetti, setShowConfetti] = useState(true);
  
  // Récupérer les données passées via state
  const { email, prenom } = location.state || {};
  
  // Prénom à afficher (avec fallback)
  const displayName = prenom || 'ami(e)';

  // Countdown et redirection automatique
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Arrêter les confettis après 5 secondes
    const confettiTimer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);

    return () => {
      clearInterval(timer);
      clearTimeout(confettiTimer);
    };
  }, [navigate]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative overflow-x-hidden overflow-y-auto"
      style={{ 
        background: 'linear-gradient(135deg, #0a0015 0%, #1a0030 50%, #0d0020 100%)' 
      }}
    >
      {/* Effets spatiaux */}
      <SpaceStars />
      {showConfetti && <Confetti />}
      
      {/* Halo lumineux central */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 50% 30%, rgba(255, 215, 0, 0.15) 0%, transparent 50%)'
        }}
      />

      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", duration: 0.8 }}
        className="text-center max-w-lg w-full relative z-10"
      >
        {/* Logo animé */}
        <motion.div
          animate={{ 
            y: [0, -10, 0],
            rotate: [0, 2, -2, 0]
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="mb-6"
        >
          <img 
            src={LOGO_URL} 
            alt="La Petite Épicerie" 
            className="h-20 md:h-24 mx-auto"
            style={{ 
              filter: 'drop-shadow(0 0 30px rgba(255, 215, 0, 0.8)) drop-shadow(0 0 60px rgba(255, 0, 255, 0.4))'
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </motion.div>

        {/* Icône de succès avec effet */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", delay: 0.3, duration: 0.8 }}
          className="mb-6"
        >
          <motion.div
            animate={{
              boxShadow: [
                '0 0 20px rgba(0, 255, 0, 0.5)',
                '0 0 60px rgba(0, 255, 0, 0.8)',
                '0 0 20px rgba(0, 255, 0, 0.5)'
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
            className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-emerald-600"
          >
            <CheckCircle className="w-14 h-14 text-white" />
          </motion.div>
        </motion.div>

        {/* Titre principal avec prénom */}
        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-4xl md:text-5xl font-bold text-white mb-2"
          style={{
            textShadow: '0 0 30px rgba(255, 215, 0, 0.5)'
          }}
        >
          Merci {displayName} ! 🎉
        </motion.h1>

        {/* Sous-titre positif */}
        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-xl text-gray-300 mb-6"
        >
          Bienvenue dans la famille de La Petite Épicerie !
        </motion.p>

        {/* Message principal - Positif et lié à l'épicerie */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="backdrop-blur-xl rounded-3xl p-6 mb-6"
          style={{
            background: 'rgba(255, 215, 0, 0.1)',
            border: '2px solid rgba(255, 215, 0, 0.5)',
            boxShadow: '0 0 40px rgba(255, 215, 0, 0.2)'
          }}
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <Mail className="w-6 h-6 text-yellow-400" />
            <span className="text-xl text-white font-semibold">Vérifiez votre boîte mail !</span>
          </div>
          
          <p className="text-gray-200 text-lg mb-4">
            Un email de confirmation vient de vous être envoyé à :
          </p>
          
          <motion.div
            animate={{
              textShadow: [
                '0 0 10px rgba(0, 255, 255, 0.5)',
                '0 0 20px rgba(0, 255, 255, 1)',
                '0 0 10px rgba(0, 255, 255, 0.5)'
              ]
            }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="text-cyan-400 font-bold text-lg mb-4 break-all"
          >
            📧 {email || 'votre adresse email'}
          </motion.div>

          <p className="text-green-400 font-semibold text-lg mb-4">
            👉 Cliquez sur le lien dans l'email pour recevoir votre code <span className="text-yellow-400">-10%</span> !
          </p>

          <div className="bg-black/30 rounded-xl p-4">
            <div className="flex items-center gap-2 text-yellow-300 mb-2">
              <Sparkles className="w-5 h-5" />
              <span className="font-semibold">Conseil de Pixie :</span>
            </div>
            <p className="text-gray-300 text-sm">
              Vérifiez vos <span className="text-yellow-400 font-bold">spams</span> si vous ne trouvez pas l'email, 
              et ajoutez-nous dans vos contacts pour ne rien manquer ! 💌
            </p>
          </div>
        </motion.div>

        {/* Section positive - Ce qui vous attend */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mb-6"
        >
          <p className="text-gray-400 text-sm mb-3">Ce qui vous attend chez nous :</p>
          <div className="flex justify-center gap-6 text-sm">
            <div className="flex items-center gap-2 text-cyan-400">
              <ShoppingBag className="w-4 h-4" />
              <span>+500 produits</span>
            </div>
            <div className="flex items-center gap-2 text-green-400">
              <Clock className="w-4 h-4" />
              <span>7j/7</span>
            </div>
            <div className="flex items-center gap-2 text-pink-400">
              <Heart className="w-4 h-4" />
              <span>Livraison gratuite</span>
            </div>
          </div>
        </motion.div>

        {/* 💜 BONUS HEARTBEATS - Section subtile */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.75 }}
          className="mb-6"
        >
          <motion.a
            href="https://heartbeats.fr/auth"
            target="_blank"
            rel="noopener noreferrer"
            className="block backdrop-blur-sm rounded-2xl p-4 transition-all hover:scale-[1.02]"
            style={{
              background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
              border: '1px solid rgba(236, 72, 153, 0.3)',
            }}
            whileHover={{
              boxShadow: '0 0 30px rgba(236, 72, 153, 0.3)'
            }}
            data-testid="heartbeats-bonus-link"
          >
            <div className="flex items-center justify-center gap-3">
              <span className="text-2xl">💜</span>
              <div className="text-left">
                <p className="text-pink-400 font-semibold text-sm">Bonus : Tu es célibataire ?</p>
                <p className="text-gray-400 text-xs">Découvre Heartbeats, notre app de rencontres locales</p>
              </div>
              <motion.span
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="text-pink-400"
              >
                →
              </motion.span>
            </div>
          </motion.a>
        </motion.div>

        {/* Compteur de redirection */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mb-6"
        >
          <p className="text-gray-400 text-sm mb-2">
            Redirection automatique dans...
          </p>
          <motion.div
            key={countdown}
            initial={{ scale: 1.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-4xl font-bold text-yellow-400"
          >
            {countdown}
          </motion.div>
        </motion.div>

        {/* Bouton retour accueil */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.9 }}
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-8 py-3 rounded-xl font-bold text-black"
            style={{
              background: 'linear-gradient(135deg, #FFD700 0%, #FF8C00 100%)',
              boxShadow: '0 0 20px rgba(255, 215, 0, 0.4)'
            }}
          >
            <Home className="w-5 h-5" />
            Découvrir l'épicerie
          </motion.button>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-8"
        >
          <p className="text-gray-500 text-sm">
            La Petite Épicerie • Vermelles<br/>
            <span className="text-cyan-400">Ouvert 7j/7 de midi à minuit</span>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}
